<template lang='pug'>
div.canvas-container
  //- id attribute is necessary or error occurs when Unity loader tries to query by ID
  canvas(ref="unityCanvas" id="unity-canvas" :style="{ background: background, opacity: canvasOpacity }")
  slot
</template>

<script>

import scriptLoader from '../util/scriptLoader'

const uncompressedFileNamesPublic = {
  loader: 'public.loader.js',
  data: 'public.data',
  framework: 'public.framework.js',
  code: 'public.wasm'
}
const uncompressedFileNamesWeb = {
  loader: 'web.loader.js',
  data: 'web.data',
  framework: 'web.framework.js',
  code: 'web.wasm'
}
const serverFileNames = {
  loader: 'web.loader.js',
  data: 'web.data.gz',
  framework: 'web.framework.js.gz',
  code: 'web.wasm.gz'
}

export default {
  name: 'Unity3dContainer',
  props: {
    autoloadUnity: { type: Boolean, default: false },
    suppressUnity: { type: Boolean, default: false },
    background: { type: String, default: '#000' },
    canvasOpacity: { type: Number, default: 1 },
    buildPathUri: { type: String, default: '' },
    loaderDirPath: { type: String, default: '' },
    companyName: String,
    productName: String,
    productVersion: String,
    matchWebGLToCanvasSize: { type: Boolean, default: false },
    devicePixelRatio: { type: Number, default: 1 },
    freeInput: { type: Boolean },
    filePrefixPublic: { type: Boolean, default: false }
  },
  data: () => ({
    isMobile: false
  }),
  computed: {
    uncompressedFileNames: function () { return this.filePrefixPublic ? uncompressedFileNamesPublic : uncompressedFileNamesWeb },
    buildFileNames: function () { return this.buildPathUri.startsWith('/') ? this.uncompressedFileNames : serverFileNames },
    loaderFileName: function () { return this.buildFileNames.loader },
    dataFileName: function () { return this.buildFileNames.data },
    frameworkFileName: function () { return this.buildFileNames.framework },
    codeFileName: function () { return this.buildFileNames.code }
  },
  mounted () {
    if (this.freeInput) {
      this.$refs.unityCanvas.setAttribute('tabindex', '1')
    }

    if (this.autoloadUnity) {
      this.loadUnity()
    }
  },
  methods: {
    loadUnity () {
      const isNotProd = process.env.NODE_ENV !== 'production'
      if (isNotProd && this.suppressUnity) {
        this.$emit('load-complete', {
          isMock: true,
          unityInstance: {
            SendMessage: (unityObj, objMethod, data) => { console.log(`Send ${data} to ${unityObj}.${objMethod}`) },
            Quit: () => {}
          }
        })
      } else {
        this._loadUnity()
      }
    },
    _loadUnity () {
      const loaderPathUri = this.buildPathUri.startsWith('/') || !this.loaderDirPath ? this.buildPathUri : this.loaderDirPath
      scriptLoader.loadHeadScript(
        `${loaderPathUri}/${this.loaderFileName}`,
        () => this.onUnityLoaded(),
        () => this.onUnityLoadError()
      )
    },
    onUnityLoaded () {
      const canvas = this.$refs.unityCanvas

      let devicePixelRatio
      this.isMobile = this.$vuetify.breakpoint.mobile
      if (this.isMobile) {
        // Avoid draining fillrate performance on mobile devices,
        // and default/override low DPI mode on mobile browsers.
        devicePixelRatio = this.devicePixelRatio
      }

      const assetPath = this.buildPathUri
      const config = {
        dataUrl: `${assetPath}/${this.dataFileName}`,
        frameworkUrl: `${assetPath}/${this.frameworkFileName}`,
        codeUrl: `${assetPath}/${this.codeFileName}`,
        // memoryUrl: "Build/{{{ MEMORY_FILENAME }}}",
        // symbolsUrl: "Build/{{{ SYMBOLS_FILENAME }}}",
        streamingAssetsUrl: 'StreamingAssets',
        companyName: this.companyName,
        productName: this.productName,
        productVersion: this.productVersion,
        // matchWebGLToCanvasSize: this.matchWebGLToCanvasSize, // Uncomment this to separately control WebGL canvas render size and DOM element size.
        devicePixelRatio
      }

      const emitProgress = progress => this.$emit('load-progress', { progress })

      /* global createUnityInstance */
      createUnityInstance(canvas, config, progress => emitProgress(progress))
        .then(unityInstance => {
          this._unityInstance = unityInstance

          this.$emit('load-complete', { unityInstance })

          this.focusOnUnity()
        })
        .catch(message => {
          this.$emit('unity-start-error', { message })
        })
    },
    onUnityLoadError () {
      this.$emit('script-load-error', {})
    },
    resizeCanvas ({ height, width }) {
      const canvas = this.$refs.unityCanvas
      canvas.height = height
      canvas.width = width
    },
    focusOnUnity () {
      this.$refs.unityCanvas.focus()
    }
  }
}
</script>

<style lang="scss" scoped>
.canvas-container {
  position: relative;
  display: flex;

  canvas {
    outline: none;
  }
}

.canvas-container > * {
  max-width: 100vw;
  flex-grow: 1;
}
</style>
